import { Eye } from 'lucide-react';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DraftDocxViewer } from '@/components/drafts/DraftDocxViewer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileWordIcon } from '@/components/ui/icons';
import { useDraftDocumentTemplateDocx } from '@/hooks/useDraftDocuments';
import { cn } from '@/lib/utils';
import {
  templateChoiceSubmitPayload,
  templateChoicesFromMessage,
  type ActiveTemplateChoices,
  type TemplateChoiceOption,
  type TemplateChoiceSelection,
} from '@/components/case-partner/template-choices';

interface TemplateChoicesPanelProps {
  allowNone?: boolean;
  className?: string;
  disabled?: boolean;
  matterGuid?: string;
  onSubmit: (value: string, selection: TemplateChoiceSelection) => void;
  question: string;
  templates: TemplateChoiceOption[];
}

const scopeLabel = (scope?: string) => {
  if (scope === 'system') return 'Crimson';
  if (scope === 'tenant') return 'Firm';
  if (scope === 'matter') return 'Matter';
  return scope || null;
};

export { templateChoicesFromMessage };

export const getActiveTemplateChoices = (
  messages: Array<{ role: string; message_metadata?: unknown }>,
): ActiveTemplateChoices | null => {
  const lastUserIndex = messages.reduce(
    (latestIndex, message, index) => (message.role === 'user' ? index : latestIndex),
    -1,
  );

  for (let index = messages.length - 1; index > lastUserIndex; index -= 1) {
    const message = messages[index];
    if (message.role !== 'assistant') continue;

    const templateChoices = templateChoicesFromMessage(message);
    if (templateChoices) return templateChoices;
  }

  return null;
};

const choiceRowClassName = (
  disabled: boolean,
) => cn(
  'flex min-w-0 items-center gap-2 rounded-md px-1.5 py-2.5 transition-colors',
  'hover:bg-[#eef3fb]/70',
  disabled && 'pointer-events-none opacity-50',
);

const ChoiceIcon: React.FC<{
  children: React.ReactNode;
  tone?: 'word' | 'blank';
}> = ({ children, tone = 'word' }) => (
  <span
    className={cn(
      'flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border',
      tone === 'word'
        ? 'border-blue-100 bg-blue-50 text-blue-700'
        : 'border-dashed border-blue-200 bg-white text-blue-500',
    )}
  >
    {children}
  </span>
);

const ChoiceCopy: React.FC<{
  meta?: string | null;
  subtitle?: string | null;
  title: string;
}> = ({ meta, subtitle, title }) => (
  <span className="min-w-0 flex-1">
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="truncate text-[13px] font-medium leading-4 text-slate-800">
        {title}
      </span>
      {meta ? (
        <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-slate-400">
          {meta}
        </span>
      ) : null}
    </span>
    {subtitle ? (
      <span className="mt-px line-clamp-2 block text-xs leading-4 text-slate-500">
        {subtitle}
      </span>
    ) : null}
  </span>
);

const TemplateChoiceCard: React.FC<{
  disabled: boolean;
  onPreview: (template: TemplateChoiceOption) => void;
  onSelect: (template: TemplateChoiceOption) => void;
  previewDisabled?: boolean;
  template: TemplateChoiceOption;
}> = memo(({ disabled, onPreview, onSelect, previewDisabled = false, template }) => {
  const handleSelect = useCallback(() => {
    onSelect(template);
  }, [onSelect, template]);

  const handlePreview = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onPreview(template);
  }, [onPreview, template]);

  const subtitle = template.description || template.when_to_use;
  const scope = scopeLabel(template.scope);

  return (
    <div className={choiceRowClassName(disabled)}>
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        disabled={disabled}
        onClick={handleSelect}
      >
        <ChoiceIcon>
          <FileWordIcon className="h-3.5 w-3.5" />
        </ChoiceIcon>
        <ChoiceCopy
          meta={scope}
          subtitle={subtitle}
          title={template.title}
        />
      </button>
      <button
        type="button"
        className={cn(
          'inline-flex h-7 w-[4.75rem] shrink-0 items-center justify-center gap-1 rounded-md bg-white/80 px-2 text-[11px] font-medium leading-4 text-slate-600 ring-1 ring-slate-200/80',
          'transition-all hover:text-slate-950 hover:ring-[#b9c6e7]',
          'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-slate-600 disabled:hover:ring-slate-200/80',
        )}
        disabled={disabled || previewDisabled}
        onClick={handlePreview}
      >
        <Eye className="h-3.5 w-3.5" />
        Preview
      </button>
    </div>
  );
});
TemplateChoiceCard.displayName = 'TemplateChoiceCard';

const PreviewButtonChrome: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex h-7 w-[4.75rem] shrink-0 items-center justify-center gap-1 px-2 text-[11px] font-medium leading-4">
    {children}
  </span>
);

const BlankDraftChoice: React.FC<{
  disabled: boolean;
  onSelect: () => void;
}> = memo(({ disabled, onSelect }) => (
  <button
    type="button"
    className={cn(choiceRowClassName(disabled), 'w-full text-left')}
    disabled={disabled}
    onClick={onSelect}
  >
    <ChoiceIcon tone="blank">
      <FileWordIcon className="h-3.5 w-3.5" />
    </ChoiceIcon>
    <ChoiceCopy
      subtitle="Start from a blank Word draft."
      title="Draft without a template"
    />
    <PreviewButtonChrome />
  </button>
));
BlankDraftChoice.displayName = 'BlankDraftChoice';

const TemplatePreviewDialog: React.FC<{
  matterGuid?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  template: TemplateChoiceOption | null;
}> = ({ matterGuid, onOpenChange, open, template }) => {
  const {
    documentBuffer,
    isError,
    isLoading,
  } = useDraftDocumentTemplateDocx(matterGuid, open ? template?.template_guid : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(88vh,820px)] w-[min(96vw,980px)] max-w-none flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-5 py-4 pr-12 text-left">
          <DialogTitle className="truncate text-base">
            {template?.title || 'Template preview'}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {template?.description || template?.when_to_use || 'Read-only Word preview of this template.'}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 bg-slate-50">
          {template ? (
            <DraftDocxViewer
              buffer={documentBuffer}
              documentId={`case-partner-template-${template.template_guid}`}
              emptyClassName="flex h-full min-h-[320px] items-center justify-center bg-slate-50/80 px-6 text-center text-sm text-slate-500"
              error={isError}
              fitToWidth
              showZoomControls
              frameClassName="draft-docx-editor h-full min-h-0 overflow-auto bg-slate-50"
              isLoading={isLoading}
              placeholderClassName="h-full min-h-0"
              portalZIndex={100000}
              title={template.title}
              versionKey={template.template_guid}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TemplateChoicesPanel: React.FC<TemplateChoicesPanelProps> = memo(({
  allowNone = true,
  className,
  disabled = false,
  matterGuid,
  onSubmit,
  question,
  templates,
}) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<TemplateChoiceOption | null>(null);
  const hasSubmittedRef = useRef(false);
  const templatesSignature = useMemo(() => (
    [
      question,
      allowNone ? 'none' : 'required',
      templates.map((template) => template.template_guid).join('|'),
    ].join(':')
  ), [allowNone, question, templates]);
  const previousSignatureRef = useRef(templatesSignature);

  useEffect(() => {
    if (previousSignatureRef.current === templatesSignature) return;

    previousSignatureRef.current = templatesSignature;
    setHasSubmitted(false);
    hasSubmittedRef.current = false;
    setPreviewTemplate(null);
  }, [templatesSignature]);

  const isDisabled = disabled || hasSubmitted;

  const submitChoice = useCallback((template: TemplateChoiceOption | null) => {
    if (disabled || hasSubmittedRef.current) return;

    hasSubmittedRef.current = true;
    setHasSubmitted(true);
    setPreviewTemplate(null);
    const payload = templateChoiceSubmitPayload(template);
    onSubmit(payload.content, payload.selection);
  }, [disabled, onSubmit]);

  const handleSelect = useCallback((template: TemplateChoiceOption) => {
    submitChoice(template);
  }, [submitChoice]);

  const handleSelectNone = useCallback(() => {
    submitChoice(null);
  }, [submitChoice]);

  const handlePreview = useCallback((template: TemplateChoiceOption) => {
    setPreviewTemplate(template);
  }, []);

  if (templates.length === 0) return null;

  return (
    <div className={cn('w-full max-w-full', className)}>
      <div className="relative w-full overflow-hidden rounded-xl border border-[#dfe6f5] bg-[linear-gradient(180deg,#fbfdff_0%,#f7f9fd_100%)] px-4 py-3 shadow-[0_14px_36px_rgba(38,52,93,0.08)]">
        <div className="flex w-full flex-col gap-2">
          <div className="text-[12px] font-semibold leading-4 text-[#52607a]">
            {question}
          </div>
          <div className="flex w-full flex-col divide-y divide-[#dfe6f2]/80">
            {templates.map((template) => (
              <TemplateChoiceCard
                key={template.template_guid}
                disabled={isDisabled}
                onPreview={handlePreview}
                onSelect={handleSelect}
                previewDisabled={!matterGuid}
                template={template}
              />
            ))}
            {allowNone ? (
              <BlankDraftChoice
                disabled={isDisabled}
                onSelect={handleSelectNone}
              />
            ) : null}
          </div>
        </div>
      </div>
      <TemplatePreviewDialog
        matterGuid={matterGuid}
        onOpenChange={(open) => {
          if (!open) setPreviewTemplate(null);
        }}
        open={Boolean(previewTemplate)}
        template={previewTemplate}
      />
    </div>
  );
});
TemplateChoicesPanel.displayName = 'TemplateChoicesPanel';
